
import { defineComponent } from "vue";
import { useRoute } from "vue-router";
import store from "@/store";

import type {
    ICourseGroupCapacityRulesModel,
    IAcademicYearModel,
    ICoursesModel
} from "@/interfaces/modules";

import CourseGroupCapacityRulesService from "@/api/Aula/CourseGroupCapacityRulesService";
import AcademicYearsService from "@/api/Configuration/AcademicYearService";
import CoursesService from "@/api/Aula/CoursesSevice";
import { SHOW_PRESCHOOL } from "@/enums";

export default defineComponent({
    name: "CourseGroupCapacityRulesContent",
    data() {
        const route = useRoute();

        return {
            // Id que viene por ruta (normalmente CourseId)
            courseId: route.params.Id || 0,
            academicYears: [] as IAcademicYearModel[],
            courses: [] as ICoursesModel[],
            form: {
                loading: true,
                dialog: false,
                wasValidated: false,
                valid: false,
                record: {} as ICourseGroupCapacityRulesModel,
                rules: {
                    required: [(v: any) => !!v || "Este campo es requerido"],
                },
            },
            table: {
                filters: { search: "" },
                currentPage: 1,
                loading: false,
                headers: [
                    { key: "CourseGroupName", label: "Curso", sortable: false },
                    { key: "AcademicYearName", label: "Año lectivo", sortable: false },
                    { key: "MinStudents", label: "Mínimo", sortable: true },
                    { key: "MaxStudents", label: "Máximo", sortable: true },
                    { key: "TargetStudents", label: "Objetivo", sortable: true },
                    { key: "Enable", label: "Activo", sortable: true },
                    { key: "CreatedByFormat", label: "Creación", sortable: true },
                    { key: "ModifiedByFormat", label: "Última modificación", sortable: true },
                    { key: "action", label: "Acciones", sortable: false },
                ],
                records: [] as ICourseGroupCapacityRulesModel[],
                rowsPerPage: 10,
            } as any,
        };
    },
    watch: {
        "form.record.CourseId"(val: any) {
            this.form.wasValidated = !val || !this.form.record.CourseId;
        },
    },
    computed: {
        filteredItems(): ICourseGroupCapacityRulesModel[] {
            const q = (this.table.filters.search ?? "").toLowerCase().trim();

            return this.table.records.filter((item: ICourseGroupCapacityRulesModel) => {
                if (!q) return true;

                const groupName = this.getCourseGroupName(item.CourseId) ?? "";
                const yearName = this.getAcademicYearName(item.AcademicYearId) ?? "";

                return [
                    groupName,
                    yearName,
                    item.MinStudents?.toString(),
                    item.MaxStudents?.toString(),
                    item.TargetStudents?.toString(),
                    item.Comments,
                    (item as any).CreatedByName,
                    (item as any).ModifiedByName,
                ]
                    .filter(Boolean)
                    .some(v => String(v).toLowerCase().includes(q));
            });
        },
        formRecord(): any {
            return this.$refs.formRecord;
        },
        rows(): any {
            return this.table.records?.length || 0;
        },
    },
    async mounted() {
        await this.loadLookups();
        await this.loadRules();
        this.form.loading = false;
    },
    methods: {
        getCourseGroupName(id?: number) {
            if (!id) return "";
            const found = this.courses.find(c => c.Id === id);
            return found?.Name || found?.Description || "";
        },
        getAcademicYearName(id?: number) {
            if (!id) return "";
            const found = this.academicYears.find(y => y.Id === id);
            // Usa Description o Name, según cómo tengas la entidad
            return (found as any)?.Name || "";
        },
        async loadLookups() {
            this.academicYears = await AcademicYearsService.GetAcademicYears();
            this.courses = await CoursesService.GetCourses(SHOW_PRESCHOOL.No);
        },
        async loadRules() {
            this.table.loading = true;
            const id = parseInt(this.courseId as any);

            if (id > 0) {
                // Trae la regla asociada al grupo (si existe)
                const res = await CourseGroupCapacityRulesService.GetByCourseGroupId(id as any);

                if (res) {
                    this.form.record = res;
                    this.table.records = [res];
                } else {
                    this.form.record = {
                        Enable: true,
                        CourseId: id,
                    } as ICourseGroupCapacityRulesModel;
                    this.table.records = [];
                }
            } else {
                // Si no viene Id por ruta, solo dejamos el formulario preparado para alta
                this.form.record = {
                    Enable: true,
                } as ICourseGroupCapacityRulesModel;
                this.table.records = [];
            }

            this.table.loading = false;
        },
        openModal() {
            const id = parseInt(this.courseId as any);

            this.form.record = {
                Enable: true,
                CourseId: id > 0 ? id : undefined,
            } as ICourseGroupCapacityRulesModel;

            this.form.dialog = true;
        },
        edit(data: ICourseGroupCapacityRulesModel) {
            // Para editar usamos el mismo registro de la tabla
            this.form.record = { ...data };
            this.form.dialog = true;
        },
        async handleSubmit(event: any) {
            event.preventDefault();

            try {
                const res = await CourseGroupCapacityRulesService.Post(this.form.record);

                this.form.record = res;
                this.form.dialog = false;

                store.dispatch("core/popupDialog", {
                    text: "Registro de capacidad de grupo guardado con éxito.",
                    type: "success",
                    showCancelButton: false,
                });

                await this.loadRules();
            } catch (err) {
                store.dispatch("core/popupDialog", {
                    text: `Error ${err}`,
                    type: "error",
                    showCancelButton: false,
                });
            }
        },
    },
});
